import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { FileDown, FileSpreadsheet, FileText, FileUp, Pencil, Search, Trash2 } from "lucide-react";
import { api, API, fmtErr } from "@/api";
import { useLang } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const fields = ["tingkatan", "tahun_lulus", "desa", "rt", "rw", "kecamatan", "kabupaten", "provinsi", "aktivitas", "wa"];

export default function AlumniTab() {
  const { t } = useLang();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [dlg, setDlg] = useState(null);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/alumni").then((r) => setItems(r.data)).catch((e) => toast.error(fmtErr(e)));
  useEffect(() => { load(); }, []);

  const filtered = items.filter((u) => u.name.toLowerCase().includes(q.toLowerCase()) || u.email.includes(q.toLowerCase()));

  const open = (u) => {
    setForm({ name: u.name, email: u.email, ...(u.alumni_profile || {}) });
    setDlg(u);
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.put(`/admin/alumni/${dlg.id}`, form);
      toast.success(t("common.saved"));
      setDlg(null);
      load();
    } catch (err) { toast.error(fmtErr(err)); }
    setBusy(false);
  };

  const remove = async (id) => {
    if (!window.confirm(t("common.confirm_delete"))) return;
    try {
      await api.delete(`/admin/alumni/${id}`);
      toast.success(t("common.deleted"));
      load();
    } catch (err) { toast.error(fmtErr(err)); }
  };

  const fileRef = useRef(null);
  const importExcel = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data } = await api.post("/admin/alumni/import", fd);
      toast.success(`${t("alumni.import_done")} — ${data.created} ditambahkan, ${data.skipped.length} dilewati, ${data.errors.length} gagal`);
      const issues = [...data.skipped, ...data.errors];
      if (issues.length) toast.info(issues.slice(0, 5).join(" · "), { duration: 9000 });
      load();
    } catch (err) { toast.error(fmtErr(err)); }
  };

  const addr = (p) => [p?.desa, p?.rt && `RT ${p.rt}`, p?.rw && `RW ${p.rw}`, p?.kecamatan, p?.kabupaten, p?.provinsi].filter(Boolean).join(", ");

  return (
    <div data-testid="alumni-tab">
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="..." className="pl-9" data-testid="alumni-search-input" />
        </div>
        <Button variant="outline" size="sm" className="rounded-full" onClick={() => window.open(`${API}/admin/alumni/template.xlsx`, "_blank")} data-testid="alumni-template-button">
          <FileDown className="h-4 w-4 mr-1.5" />{t("alumni.template")}
        </Button>
        <Button variant="outline" size="sm" className="rounded-full" onClick={() => fileRef.current?.click()} data-testid="alumni-import-button">
          <FileUp className="h-4 w-4 mr-1.5" />{t("alumni.import")}
        </Button>
        <Button variant="outline" size="sm" className="rounded-full" onClick={() => window.open(`${API}/admin/alumni/export.xlsx`, "_blank")} data-testid="alumni-export-xlsx-button">
          <FileSpreadsheet className="h-4 w-4 mr-1.5" />{t("alumni.export_xlsx")}
        </Button>
        <Button variant="outline" size="sm" className="rounded-full" onClick={() => window.open(`${API}/admin/alumni/export.pdf`, "_blank")} data-testid="alumni-export-pdf-button">
          <FileText className="h-4 w-4 mr-1.5" />{t("alumni.export_pdf")}
        </Button>
        <input ref={fileRef} type="file" accept=".xlsx" className="hidden" onChange={importExcel} data-testid="alumni-import-input" />
      </div>
      <div className="bg-white rounded-xl border border-emerald-900/10 shadow-sm overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("auth.name")}</TableHead>
              <TableHead>{t("alumni.tingkatan")}</TableHead>
              <TableHead>{t("alumni.tahun_lulus")}</TableHead>
              <TableHead>{t("alumni.domisili")}</TableHead>
              <TableHead>{t("alumni.aktivitas")}</TableHead>
              <TableHead>WA</TableHead>
              <TableHead>Email</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((u) => (
              <TableRow key={u.id} data-testid={`alumni-row-${u.id}`}>
                <TableCell className="font-medium">{u.name}</TableCell>
                <TableCell>{u.alumni_profile?.tingkatan}</TableCell>
                <TableCell>{u.alumni_profile?.tahun_lulus}</TableCell>
                <TableCell className="text-xs max-w-[220px]">{addr(u.alumni_profile)}</TableCell>
                <TableCell className="text-xs">{u.alumni_profile?.aktivitas}</TableCell>
                <TableCell className="text-xs">{u.alumni_profile?.wa}</TableCell>
                <TableCell className="text-xs">{u.email}</TableCell>
                <TableCell>
                  <div className="flex gap-1.5">
                    <Button size="sm" variant="outline" onClick={() => open(u)} data-testid={`alumni-edit-${u.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                    <Button size="sm" variant="destructive" onClick={() => remove(u.id)} data-testid={`alumni-delete-${u.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!dlg} onOpenChange={(o) => !o && setDlg(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-2xl text-emerald-950">{t("common.edit")} — {dlg?.name}</DialogTitle></DialogHeader>
          {form && (
            <form onSubmit={submit} className="grid grid-cols-2 gap-4 mt-2" data-testid="alumni-edit-form">
              <div className="col-span-2 sm:col-span-1">
                <Label className="text-sm font-medium text-slate-700">{t("auth.name")}</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="mt-1.5" data-testid="alumni-edit-name" />
              </div>
              <div className="col-span-2 sm:col-span-1">
                <Label className="text-sm font-medium text-slate-700">Email</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required className="mt-1.5" data-testid="alumni-edit-email" />
              </div>
              <div>
                <Label className="text-sm font-medium text-slate-700">{t("alumni.tingkatan")}</Label>
                <Select value={form.tingkatan} onValueChange={(v) => setForm({ ...form, tingkatan: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="alumni-edit-tingkatan"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["RA", "MI", "MTs", "MA"].map((tk) => <SelectItem key={tk} value={tk}>{tk}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {fields.slice(1).map((k) => (
                <div key={k}>
                  <Label className="text-sm font-medium text-slate-700">{t(`alumni.${k}`)}</Label>
                  <Input value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="mt-1.5" data-testid={`alumni-edit-${k}`} />
                </div>
              ))}
              <div className="col-span-2">
                <Button type="submit" disabled={busy} className="w-full rounded-full bg-emerald-800 hover:bg-emerald-900" data-testid="alumni-edit-submit">
                  {busy ? t("common.loading") : t("common.save")}
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
